import * as XLSX from "xlsx-js-style";

const usePayrollExport = ({
  t,
  isRTL,
  language,
  selectedMonth,
  filteredPayrollData,
  payrollData,
  totalGrossSalary,
  totalNetSalary,
  totalDeductions,
  averageKPI,
  showAlert,
}) => {
  const roundToHundredth = (value) =>
    Number((Number(value) || 0).toFixed(2));
  const roundToInt = (value) => Math.round(Number(value) || 0);

  const handleExportPayrollExcel = () => {
    try {
      // ── Constants ──────────────────────────────────────────────────────────
      const HEADER_BG = "002060"; // dark navy requested by user
      const HEADER_FG = "FFFFFF";
      const ROW_ALT_BG = "D9E1F2"; // light blue requested by user
      const ROW_DEF_BG = "FFFFFF"; // white – odd data rows (row 3,5,7…)
      const FONT_NAME = "Arial";
      const FONT_SIZE = 11;
      const MONEY_FMT = "#,##0";
      const DATE_FMT = "DD/MM/YYYY";

      const thinBorder = (color = "C0C0C0") => ({
        top: { style: "thin", color: { rgb: color } },
        bottom: { style: "thin", color: { rgb: color } },
        left: { style: "thin", color: { rgb: color } },
        right: { style: "thin", color: { rgb: color } },
      });

      // ── Column definitions (A=0 … Y=24) ───────────────────────────────────
      // type: "text" | "number" | "money" | "formula" | "date"
      // align: "left" | "center" | "right"
      const getLabel = (key, enLabel, arLabel) => {
        const trans = t(key);
        if (trans !== key) return trans;
        return isRTL ? arLabel : enLabel;
      };

      const COLS = [
        { key: "infinitycode", label: getLabel("infinitycode", "ID", "رقم الموظف"), wch: 8, align: "center", type: "number" },
        { key: "englishName", label: getLabel("englishName", "Employee Name", "اسم الموظف"), wch: 35, align: "left", type: "text" },
        { key: "arabicName", label: getLabel("arabicName", "Arabic Name", "الاسم بالعربي"), wch: 28, align: "right", type: "text" },
        { key: "nationalId", label: getLabel("nationalId", "National ID", "الرقم القومي"), wch: 16, align: "center", type: "text" },
        { key: "phone", label: getLabel("phone", "Phone", "رقم التليفون"), wch: 15, align: "center", type: "text" },
        { key: "department", label: getLabel("department", "Department / Location", "القسم / الموقع"), wch: 22, align: "left", type: "text" },
        { key: "hiringDate", label: getLabel("hiringDate", "Hiring Date", "تاريخ التعيين"), wch: 14, align: "center", type: "date" },
        { key: "accountNumber", label: getLabel("accountNumber", "Account Number", "رقم الحساب"), wch: 20, align: "center", type: "text" },
        { key: "grossSalary", label: getLabel("grossSalary", "Gross Salary", "إجمالي الراتب"), wch: 14, align: "center", type: "money" },
        { key: "basicSalary", label: getLabel("basicSalary", "Basic Salary", "الراتب الأساسي"), wch: 14, align: "center", type: "money" },
        { key: "netSalary", label: getLabel("netSalary", "Net Salary", "صافي الراتب"), wch: 14, align: "center", type: "money" },
        { key: "workingDays", label: getLabel("workingDays", "Working Days", "أيام العمل"), wch: 12, align: "center", type: "number" },
        { key: "absenceDays", label: getLabel("absenceDays", "Absence Days", "أيام الغياب"), wch: 12, align: "center", type: "number" },
        { key: "absenceDeduction", label: getLabel("absenceDeduction", "Absence Value", "قيمة الغياب"), wch: 14, align: "center", type: "money" },
        { key: "bonus", label: getLabel("bonus", "Bonus", "المكافأة"), wch: 12, align: "center", type: "money" },
        { key: "overtimeTotal", label: getLabel("overtimeTotal", "Overtime Total", "إجمالي الإضافي"), wch: 14, align: "center", type: "money" },
        { key: "transportation", label: getLabel("transportationAllowance", "Transportation", "بدل الانتقال"), wch: 18, align: "center", type: "money" },
        { key: "meal", label: getLabel("mealAllowance", "Meal Allowance", "بدل الوجبات"), wch: 14, align: "center", type: "money" },
        { key: "mobile", label: getLabel("mobileAllowance", "Mobile Allowance", "بدل الموبايل"), wch: 14, align: "center", type: "money" },
        { key: "bonusAdjustments", label: getLabel("bonusAdjustments", "Bonus Adjustments", "تسويات المكافآت"), wch: 16, align: "center", type: "money" },
        { key: "otherAllowances", label: getLabel("otherAllowances", "Other Allowances", "بدلات أخرى"), wch: 15, align: "center", type: "money" },
        { key: "allowances", label: getLabel("allowances", "Total Allowances", "إجمالي البدلات"), wch: 14, align: "center", type: "money" },
        { key: "penalty", label: getLabel("penalty", "Penalty", "الجزاءات"), wch: 10, align: "center", type: "money" },
        { key: "advancePayment", label: getLabel("advancePayment", "Advance Payment", "السلف"), wch: 14, align: "center", type: "money" },
        { key: "sickLeave", label: getLabel("sickLeave", "Sick Leave 25%", "إجازة مرضية 25%"), wch: 15, align: "center", type: "money" },
        { key: "delayDeduction", label: getLabel("delayDeduction", "Delay", "التأخير"), wch: 10, align: "center", type: "money" },
        { key: "otherDeductions", label: getLabel("otherDeductions", "Other Deductions", "خصومات أخرى"), wch: 15, align: "center", type: "money" },
        { key: "insuranceSalary", label: getLabel("insuranceSalary", "Insurance Salary", "مرتب التأمين"), wch: 16, align: "center", type: "money" },
        { key: "socialInsurance", label: getLabel("socialInsurance", "Social Ins. Employee Share", "تأمين حصة الموظف"), wch: 22, align: "center", type: "money" },
        { key: "insuranceCompanyShare", label: getLabel("insuranceCompanyShare", "Social Ins. Company Share", "تأمين حصة الشركة"), wch: 22, align: "center", type: "money" },
        { key: "taxMonth", label: getLabel("taxMonth", "Tax", "ضريبة الدخل"), wch: 12, align: "center", type: "money" },
        { key: "martyrsFund", label: getLabel("martyrsFund", "Martyrs\' Families Fund", "صندوق أسر الشهداء"), wch: 20, align: "center", type: "money" },
        { key: "emergencyFund", label: getLabel("emergencyFund", "0.01 Emergency Fund", "صندوق طوارئ 1%"), wch: 18, align: "center", type: "money" },
        { key: "deductions", label: getLabel("deductions", "Total Deductions", "إجمالي الخصومات"), wch: 14, align: "center", type: "money" },

        { key: "totalEmployeeCost", label: getLabel("totalEmployeeCost", "Total Employee Cost", "إجمالي تكلفة الموظف"), wch: 20, align: "center", type: "money" },
        { key: "markup", label: getLabel("markup", "Markup", "هامش الربح"), wch: 12, align: "center", type: "money" },
        { key: "totalCost", label: getLabel("totalCost", "Total Cost", "إجمالي التكلفة"), wch: 14, align: "center", type: "money" },
        { key: "status", label: getLabel("status", "Status", "الحالة"), wch: 12, align: "center", type: "text" },
        { key: "paymentMethod", label: getLabel("paymentMethod", "Payment Method", "طريقة الدفع"), wch: 16, align: "center", type: "text" },
        { key: "paymentDate", label: getLabel("paymentDate", "Payment Date", "تاريخ الدفع"), wch: 14, align: "center", type: "date" },
      ];

      // ── Helper: 0-based column index → Excel letter(s) ─────────────────────
      const colLetter = (idx) => {
        let s = "";
        let n = idx;
        do {
          s = String.fromCharCode(65 + (n % 26)) + s;
          n = Math.floor(n / 26) - 1;
        } while (n >= 0);
        return s;
      };

      // Map key → column letter for formula building
      const COL = {};
      COLS.forEach((c, i) => { COL[c.key] = colLetter(i); });

      // ── Style builders ──────────────────────────────────────────────────────
      const makeHeaderCell = (label) => ({
        v: label,
        t: "s",
        s: {
          font: { name: FONT_NAME, sz: FONT_SIZE, bold: true, color: { rgb: HEADER_FG } },
          fill: { fgColor: { rgb: HEADER_BG } },
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
          border: thinBorder("FFFFFF"),
        },
      });

      const makeDataCell = (value, colDef, rowIdx) => {
        // rowIdx 0 = first data row = Excel row 2 (even → light blue)
        const fillColor = rowIdx % 2 === 0 ? ROW_ALT_BG : ROW_DEF_BG;
        const baseStyle = {
          font: { name: FONT_NAME, sz: FONT_SIZE, color: { rgb: "000000" } },
          fill: { fgColor: { rgb: fillColor } },
          border: thinBorder(),
          alignment: { horizontal: colDef.align, vertical: "center" },
        };

        if (colDef.type === "formula") {
          // value is the formula string
          return { t: "n", f: value, s: { ...baseStyle, numFmt: MONEY_FMT } };
        }
        if (colDef.type === "date") {
          return { v: String(value ?? ""), t: "s", s: baseStyle };
        }
        if (colDef.type === "money") {
          return { v: Number(value) || 0, t: "n", s: { ...baseStyle, numFmt: MONEY_FMT } };
        }
        if (colDef.type === "number") {
          return { v: Number(value) || 0, t: "n", s: baseStyle };
        }
        // text — ALWAYS force string type (critical for NationalID, Phone)
        return { v: String(value ?? ""), t: "s", s: baseStyle };
      };

      const makeSummaryCell = (value, isFormula = false, isLabel = false) => {
        const baseStyle = {
          font: { name: FONT_NAME, sz: FONT_SIZE, bold: true, color: { rgb: isLabel ? HEADER_FG : "000000" } },
          fill: { fgColor: { rgb: isLabel ? HEADER_BG : "FFFFFF" } },
          border: thinBorder(),
          alignment: { horizontal: "center", vertical: "center" },
        };
        if (isFormula) {
          return { t: "n", f: value, s: { ...baseStyle, numFmt: MONEY_FMT } };
        }
        if (typeof value === "string") {
          return { v: value, t: "s", s: { ...baseStyle, alignment: { horizontal: "left", vertical: "center" } } };
        }
        return { v: value, t: "n", s: { ...baseStyle, numFmt: MONEY_FMT } };
      };

      // ── Define subsets of columns for each tab ──────────────────────────────
      const INFO_KEYS = ["infinitycode", "englishName", "arabicName", "nationalId", "phone", "department", "hiringDate", "accountNumber", "workingDays", "status", "paymentMethod", "paymentDate"];
      const ALLOWANCES_KEYS = ["infinitycode", "englishName", "basicSalary", "bonus", "overtimeTotal", "transportation", "meal", "mobile", "bonusAdjustments", "otherAllowances", "allowances", "grossSalary"];
      const DEDUCTIONS_KEYS = ["infinitycode", "englishName", "basicSalary", "absenceDays", "absenceDeduction", "penalty", "advancePayment", "sickLeave", "delayDeduction", "socialInsurance", "otherDeductions", "deductions", "netSalary"];
      const INVOICE_KEYS = ["infinitycode", "englishName", "department", "hiringDate", "grossSalary", "netSalary", "insuranceSalary", "socialInsurance", "insuranceCompanyShare", "taxMonth", "martyrsFund", "emergencyFund", "totalEmployeeCost", "markup", "totalCost"];

      const INFO_COLS = COLS.filter(c => INFO_KEYS.includes(c.key));
      const ALLOWANCES_COLS = COLS.filter(c => ALLOWANCES_KEYS.includes(c.key));
      const DEDUCTIONS_COLS = COLS.filter(c => DEDUCTIONS_KEYS.includes(c.key));
      const INVOICE_COLS = COLS.filter(c => INVOICE_KEYS.includes(c.key));

      const exportYear = Number(selectedMonth?.year || new Date().getFullYear());
      const exportMonth = String(Number(selectedMonth?.month || new Date().getMonth() + 1)).padStart(2, "0");

      // ── Period title string ─────────────────────────────────────────────────
      const getPeriodString = () => {
        const fmt = (d) => {
          const dt = new Date(d);
          if (isNaN(dt)) return "";
          return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
        };
        const firstEmp = filteredPayrollData[0];
        if (firstEmp?.payrollStartDate && firstEmp?.payrollEndDate) {
          const start = fmt(firstEmp.payrollStartDate);
          const end = fmt(firstEmp.payrollEndDate);
          if (start && end) {
            return isRTL
              ? `فترة الرواتب: من ${start} إلى ${end}`
              : `Payroll Period: ${start} – ${end}`;
          }
        }
        const y = exportYear;
        const m = Number(exportMonth);
        const firstDay = `01/${String(m).padStart(2, "0")}/${y}`;
        const lastDayDate = new Date(y, m, 0);
        const lastDay = `${String(lastDayDate.getDate()).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
        return isRTL
          ? `فترة الرواتب: من ${firstDay} إلى ${lastDay}`
          : `Payroll Period: ${firstDay} – ${lastDay}`;
      };
      const periodString = getPeriodString();

      // ── Build worksheet Helper ────────────────────────────────────────────────
      const buildWorksheet = (sheetCols, periodTitle) => {
        const ws = {};
        const numEmployees = filteredPayrollData.length;
        const headerRow = 2;
        const firstDataRow = 3;
        const lastDataRow = firstDataRow + numEmployees - 1;
        const summaryRow = lastDataRow + 1; // directly below data rows

        const sheetColLetters = {};
        sheetCols.forEach((c, i) => { sheetColLetters[c.key] = colLetter(i); });

        // Title row (Excel row 1) – merged across all columns
        ws["A1"] = {
          v: periodTitle,
          t: "s",
          s: {
            font: { name: FONT_NAME, sz: 13, bold: true, color: { rgb: HEADER_FG } },
            fill: { fgColor: { rgb: "1F4E79" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: thinBorder("FFFFFF"),
          },
        };

        // Header row (Excel row 2)
        sheetCols.forEach((col, colIdx) => {
          ws[`${colLetter(colIdx)}${headerRow}`] = makeHeaderCell(col.label);
        });

        // Data rows (start at row 3)
        filteredPayrollData.forEach((emp, rowIdx) => {
          const R = firstDataRow + rowIdx;
          let payDateStr = "";
          if (emp.paymentDate) {
            const d = new Date(emp.paymentDate);
            if (!isNaN(d)) {
              payDateStr = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
            } else {
              payDateStr = String(emp.paymentDate);
            }
          } else {
            const now = new Date();
            payDateStr = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
          }

          const rawValues = {
            infinitycode: emp.infinitycode || emp.infinityCode || emp.code || "",
            englishName: emp.englishName || emp.employeeName || "",
            arabicName: emp.arabicName || "",
            nationalId: String(emp.personalInfo?.nationalId || emp.nationalId || ""),
            phone: String(emp.personalInfo?.phone || emp.phoneNumber || ""),
            department: emp.department || "",
            hiringDate: (() => {
              const hd = emp.hiringDate;
              if (!hd) return "";
              const d = new Date(hd);
              if (isNaN(d)) return String(hd);
              return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
            })(),
            accountNumber: String(emp.bankInfo?.accountNumber || emp.accountNumber || ""),
            grossSalary: roundToInt(emp.grossSalary),
            netSalary: roundToInt(emp.netSalary),
            workingDays: Number(emp.workingDays) || 0,
            basicSalary: roundToInt(emp.basicSalary),
            bonus: roundToInt(emp.bonus),
            overtimeTotal: roundToInt(emp.overtimeTotal),
            transportation: roundToInt(emp.transportationAllowance),
            meal: roundToInt(emp.mealAllowance),
            mobile: roundToInt(emp.mobileAllowance),
            bonusAdjustments: roundToInt(emp.bonusAdjustments),
            otherAllowances: Math.max(0, roundToInt(emp.totalAllowances) - (roundToInt(emp.transportationAllowance) + roundToInt(emp.mealAllowance) + roundToInt(emp.mobileAllowance) + roundToInt(emp.bonusAdjustments))),
            allowances: roundToInt(emp.totalAllowances),
            absenceDays: Number(emp.absenceDays) || 0,
            absenceDeduction: roundToInt(emp.absenceDeduction),
            penalty: roundToInt(emp.penalty),
            advancePayment: roundToInt(emp.advancePayment),
            sickLeave: roundToInt(emp.sickLeaveDeduction),
            delayDeduction: roundToInt(emp.delayDeduction),
            otherDeductions: Math.max(0, roundToInt(emp.totalDeductions) - (roundToInt(emp.absenceDeduction) + roundToInt(emp.penalty) + roundToInt(emp.advancePayment) + roundToInt(emp.socialInsurance) + roundToInt(emp.sickLeaveDeduction) + roundToInt(emp.delayDeduction))),
            deductions: roundToInt(emp.totalDeductions),
            insuranceSalary: roundToInt(emp.insuranceSalary),
            socialInsurance: roundToInt(emp.socialInsurance),
            insuranceCompanyShare: roundToInt(emp.insuranceCompanyShare),
            taxMonth: roundToInt(emp.taxMonth),
            martyrsFund: roundToInt(emp.martyrsFund),
            emergencyFund: roundToInt(emp.emergencyFund),
            totalEmployeeCost: roundToInt(emp.grossSalary) + roundToInt(emp.insuranceCompanyShare) + roundToInt(emp.martyrsFund) + roundToInt(emp.emergencyFund),
            markup: roundToInt(emp.markup),
            totalCost: roundToInt(emp.grossSalary) + roundToInt(emp.insuranceCompanyShare) + roundToInt(emp.martyrsFund) + roundToInt(emp.emergencyFund) + roundToInt(emp.markup),
            status: emp.status || "pending",
            paymentMethod: emp.paymentMethod || "Bank Transfer",
            paymentDate: payDateStr,
          };

          sheetCols.forEach((col, colIdx) => {
            ws[`${colLetter(colIdx)}${R}`] = makeDataCell(rawValues[col.key], col, rowIdx);
          });
        });

        // Summary row
        sheetCols.forEach((col, colIdx) => {
          const addr = `${colLetter(colIdx)}${summaryRow}`;
          if (colIdx === 0) {
            ws[addr] = makeSummaryCell(t("netTotal") ? `${t("netTotal")} (${numEmployees})` : `NET TOTAL (${numEmployees} Employees)`, false, true);
          } else if (col.type === "money" || col.type === "formula") {
            ws[addr] = makeSummaryCell(`SUM(${sheetColLetters[col.key]}${firstDataRow}:${sheetColLetters[col.key]}${lastDataRow})`, true, true);
          } else {
            ws[addr] = {
              v: "", t: "s",
              s: {
                font: { name: FONT_NAME, sz: FONT_SIZE, bold: true, color: { rgb: HEADER_FG } },
                fill: { fgColor: { rgb: HEADER_BG } },
                border: thinBorder(),
              },
            };
          }
        });

        // Worksheet metadata
        ws["!ref"] = `A1:${colLetter(sheetCols.length - 1)}${summaryRow}`;
        ws["!cols"] = sheetCols.map((c) => ({ wch: c.wch }));
        ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: sheetCols.length - 1 } }];
        if (isRTL) ws["!dir"] = "rtl";

        // Row heights
        const rowHeightsArr = [];
        rowHeightsArr[0] = { hpt: 30 }; // title row
        rowHeightsArr[1] = { hpt: 35 }; // header row
        for (let r = 2; r <= summaryRow; r++) {
          if (!rowHeightsArr[r]) rowHeightsArr[r] = { hpt: 20 };
        }
        ws["!rows"] = rowHeightsArr;

        // Freeze title + header rows
        ws["!sheetViews"] = [{ state: "frozen", ySplit: 2, topLeftCell: "A3", activePane: "bottomLeft" }];

        return ws;
      };

      // ── Build & save workbook ────────────────────────────────────────────────
      const wb = XLSX.utils.book_new();
      
      const SALARY_SHEET_COLS = COLS.filter(c => c.key !== "accountNumber");
      const wsSalarySheet = buildWorksheet(SALARY_SHEET_COLS, periodString);
      const wsInvoice = buildWorksheet(INVOICE_COLS, periodString);
      const wsAllowances = buildWorksheet(ALLOWANCES_COLS, periodString);
      const wsDeductions = buildWorksheet(DEDUCTIONS_COLS, periodString);
      const wsInfo = buildWorksheet(INFO_COLS, periodString);

      XLSX.utils.book_append_sheet(wb, wsSalarySheet, isRTL ? "شيت الرواتب" : "Salary Sheet");
      XLSX.utils.book_append_sheet(wb, wsInvoice, isRTL ? "الفاتورة" : "Invoice");
      XLSX.utils.book_append_sheet(wb, wsAllowances, isRTL ? "البدلات" : "Allowances");
      XLSX.utils.book_append_sheet(wb, wsDeductions, isRTL ? "الخصومات" : "Deductions");
      XLSX.utils.book_append_sheet(wb, wsInfo, isRTL ? "المعلومات" : "Information");

      XLSX.writeFile(wb, `Payroll_${exportYear}-${exportMonth}.xlsx`);
      showAlert(t("success"), `${t("payrollExportSuccess")}`, "success");

    } catch (error) {
      console.error("Error exporting payroll to Excel:", error);
      showAlert(
        t("error"),
        `${t("payrollExportError")}: ${error.message}`,
        "error",
      );
    }
  };

  const handleExportPayrollPDF = async () => {
    try {
      const { default: jsPDF } = await import("jspdf");
      const { default: html2canvas } = await import("html2canvas");

      const isAr = language === "ar";
      const exportYear = Number(
        selectedMonth?.year || new Date().getFullYear(),
      );
      const exportMonth = String(
        Number(selectedMonth?.month || new Date().getMonth() + 1),
      ).padStart(2, "0");
      const totalGross = roundToInt(
        filteredPayrollData.reduce(
          (s, e) => s + (Number(e.grossSalary) || 0),
          0,
        ),
      );
      const totalDed = roundToInt(
        filteredPayrollData.reduce(
          (s, e) => s + (Number(e.totalDeductions) || 0),
          0,
        ),
      );
      const totalNet = roundToInt(
        filteredPayrollData.reduce((s, e) => s + (Number(e.netSalary) || 0), 0),
      );
      const currency = t("currency");
      const dir = isAr ? "rtl" : "ltr";

      const rowsHtml = filteredPayrollData
        .map(
          (emp, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f3f6fb"}">
        <td>${emp.infinityCode || emp.infinitycode || emp.code || "-"}</td>
        <td>${emp.englishName || emp.employeeName || "-"}</td>
        <td>${emp.arabicName || "-"}</td>
        <td>${emp.nationalId || "-"}</td>
        <td>${emp.phoneNumber || "-"}</td>
        <td>${emp.department || "-"}</td>
        <td>${roundToInt(emp.grossSalary).toLocaleString()}</td>
        <td>${roundToInt(emp.basicSalary).toLocaleString()}</td>
        <td style="font-weight:700;color:#16a34a">${roundToInt(emp.netSalary).toLocaleString()}</td>
        <td>${roundToInt(emp.workingDays)}</td>
        <td>${roundToInt(emp.absenceDays || 0)}</td>
        <td>${roundToInt(emp.absenceDeduction).toLocaleString()}</td>
        <td>${roundToInt(emp.totalAllowances || 0).toLocaleString()}</td>
        <td>${roundToInt(emp.penalty).toLocaleString()}</td>
        <td>${roundToInt(emp.advancePayment).toLocaleString()}</td>
        <td>${roundToInt(emp.socialInsurance).toLocaleString()}</td>
        <td>${roundToInt(emp.sickLeaveDeduction).toLocaleString()}</td>
        <td>${roundToInt(emp.delayDeduction).toLocaleString()}</td>
        <td>${roundToInt(emp.totalDeductions).toLocaleString()}</td>
        <td>${emp.status || "Active"}</td>
      </tr>`,
        )
        .join("");

      // ── Build an off-screen container the browser renders natively ──────────
      // Using Tahoma / Arial — both ship with Windows and include Arabic glyphs.
      const wrapper = document.createElement("div");
      wrapper.style.cssText = [
        "position:fixed",
        "left:-99999px",
        "top:0",
        "width:1122px",
        "background:#fff",
        `font-family:Tahoma,Arial,'Segoe UI',sans-serif`,
        `direction:${dir}`,
        "color:#1e293b",
        "padding:24px",
        "z-index:-9999",
      ].join(";");

      wrapper.innerHTML = `
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        .ph{text-align:center;margin-bottom:20px}
        .ph h1{font-size:20px;font-weight:700;color:#2563eb;margin-bottom:4px}
        .ph p{font-size:11px;color:#64748b}
        table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:24px}
        th{background:#2563eb;color:#fff;font-weight:700;padding:8px 6px;text-align:center}
        td{padding:7px 6px;border-bottom:1px solid #e2e8f0;text-align:center}
        .sb{border:1px solid #e2e8f0;border-radius:8px;padding:14px 18px;max-width:380px;${isAr ? "margin-right:auto" : "margin-left:auto"}}
        .sb h3{font-size:13px;font-weight:700;color:#2563eb;margin-bottom:10px}
        .sr{display:flex;justify-content:space-between;padding:4px 0;font-size:11px;border-bottom:1px solid #f1f5f9}
        .sr.net{font-size:13px;font-weight:700;border-top:2px solid #1e293b;border-bottom:none;padding-top:8px;margin-top:4px}
      </style>
      <div class="ph">
        <h1>${t("payrollDetails")}</h1>
        <p>${t("exportDate")}: ${new Date().toLocaleDateString(isAr ? "ar-EG" : "en-GB")} &nbsp;|&nbsp; ${t("payPeriod")}: ${exportYear}-${exportMonth}</p>
      </div>
      <table>
        <thead>
          <tr>
            <th>${t("infinitycode")}</th>
            <th>${t("englishName") !== "englishName" ? t("englishName") : (isRTL ? "الاسم بالانجليزي" : "English Name")}</th>
            <th>${t("arabicName")}</th>
            <th>${t("nationalId")}</th>
            <th>${t("phone")}</th>
            <th>${t("department")}</th>
            <th>${t("grossSalary") !== "grossSalary" ? t("grossSalary") : (isRTL ? "إجمالي الراتب" : "Gross Salary")}</th>
            <th>${t("basicSalary")}</th>
            <th>${t("netSalary") !== "netSalary" ? t("netSalary") : (isRTL ? "صافي الراتب" : "Net Salary")}</th>
            <th>${t("workingDays")}</th>
            <th>${t("absenceDays") !== "absenceDays" ? t("absenceDays") : (isRTL ? "أيام الغياب" : "Absence Days")}</th>
            <th>${t("absenceDeduction") !== "absenceDeduction" ? t("absenceDeduction") : (isRTL ? "قيمة الغياب" : "Absence Value")}</th>
            <th>${t("allowances")}</th>
            <th>${t("penalty") !== "penalty" ? t("penalty") : (isRTL ? "الجزاءات" : "Penalty")}</th>
            <th>${t("advancePayment") !== "advancePayment" ? t("advancePayment") : (isRTL ? "السلف" : "Advance")}</th>
            <th>${t("socialInsurance") !== "socialInsurance" ? t("socialInsurance") : (isRTL ? "التأمينات" : "Insurance")}</th>
            <th>${t("sickLeave") !== "sickLeave" ? t("sickLeave") : (isRTL ? "إجازة مرضية 25%" : "Sick Leave 25%")}</th>
            <th>${t("delay") !== "delay" ? t("delay") : (isRTL ? "تأخير" : "Delay")}</th>
            <th>${t("deductions")}</th>
            <th>${t("status")}</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="sb">
        <h3>${t("payrollSummary")}</h3>
        <div class="sr"><span>${t("employees")}:</span><span>${filteredPayrollData.length}</span></div>
        <div class="sr"><span>${t("grossTotal")}:</span><span>${totalGross.toLocaleString()} ${currency}</span></div>
        <div class="sr"><span>${t("deductions")}:</span><span>${totalDed.toLocaleString()} ${currency}</span></div>
        <div class="sr net"><span>${t("netTotal")}:</span><span>${totalNet.toLocaleString()} ${currency}</span></div>
      </div>`;

      document.body.appendChild(wrapper);

      // Let the browser paint before capturing
      await new Promise((r) => setTimeout(r, 150));

      const canvas = await html2canvas(wrapper, {
        scale: 1.8,
        useCORS: false,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: 1122,
      });

      document.body.removeChild(wrapper);

      // ── Embed canvas image into a landscape A4 PDF (with auto-pagination) ──
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: "a4",
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * pageW) / canvas.width;
      const imgData = canvas.toDataURL("image/jpeg", 0.93);

      let remaining = imgH;
      let offsetY = 0;

      pdf.addImage(imgData, "JPEG", 0, offsetY, imgW, imgH);
      remaining -= pageH;

      while (remaining > 0) {
        offsetY -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, offsetY, imgW, imgH);
        remaining -= pageH;
      }

      pdf.save(`Payroll_${exportYear}-${exportMonth}.pdf`);
      showAlert(t("success"), `${t("payrollExportSuccessPDF")}`, "success");
    } catch (error) {
      console.error("Error exporting payroll to PDF:", error);
      showAlert(
        t("error"),
        `${t("payrollExportErrorPDF")}: ${error.message}`,
        "error",
      );
    }
  };

  return {
    handleExportPayrollExcel,
    handleExportPayrollPDF,
  };
};

export default usePayrollExport;
